import React, { useState, useEffect, useRef } from 'react';
import config from '../config/config';
import maplibregl from 'maplibre-gl';

const PRINT_CONFIG = config.print;
const ATTRIBUTION_TEXT = config.common.attribution;
const DEFAULT_STYLE_NAME = config.map.defaultStyleName;
const DEFAULT_ZOOM_PRECISION = config.map.defaultZoomPrecision;

const A4_LANDSCAPE = PRINT_CONFIG.printPaperTypes.a4Landscape;
const A4_PORTRAIT = PRINT_CONFIG.printPaperTypes.a4Portrait;
const A3_LANDSCAPE = PRINT_CONFIG.printPaperTypes.a3Landscape;
const A3_PORTRAIT = PRINT_CONFIG.printPaperTypes.a3Portrait;

const MAPION_BASE_URL = config.urls.mapionBaseUrl;
const MAPION_M2_URL = MAPION_BASE_URL + process.env.PUBLIC_URL;

/** 表示用の用紙サイズ文字列 */
const getPageSizeCss = (paperSize) => {
  // cssの@page size用の有効キーワード
  switch (paperSize) {
    case A4_LANDSCAPE: return 'A4 landscape';
    case A4_PORTRAIT: return 'A4 portrait';
    case A3_LANDSCAPE: return 'A3 landscape';
    case A3_PORTRAIT: return 'A3 portrait';
    default: return 'A4 portrait';
  }
};

/** 別ウィンドウで開く印刷用HTMLを生成する処理 **/
// インラインでスタイル記述
const buildPrintHtml = ({
  memo,
  pageSizeCss,
  width,
  height,
  logoUrl,
  qrUrl,
  currentStyle,
  currentCenter,
  currentZoom,
  currentPitch,
  currentBearing
}) => `
<html>
  <head>
    <title>${PRINT_CONFIG.messages.printTitle}</title>
    <link rel="stylesheet" href="${PRINT_CONFIG.urls.maplibreCss}" />
    <style>
      body { margin: 0; padding: 0; font-family: ${PRINT_CONFIG.styles.fontFamily}; }
      .print-memo { margin-bottom: ${PRINT_CONFIG.styles.memoMarginBottom}; font-size: ${PRINT_CONFIG.styles.memoFontSize}; }

      #print-map-wrapper { position: relative; width: ${width}px; height: ${height}px; margin-bottom: ${PRINT_CONFIG.styles.mapMarginBottom}; }
      #print-map { width: 100%; height: 100%; }

      .print-map-attribution { position: absolute; left: 8px; bottom: 6px; padding: 2px 6px; font-size: 10px; line-height: 1.2; color: #000; background: transparent; border-radius: 2px; pointer-events: none; }

      @page { size: ${pageSizeCss}; margin: 8mm; }
    </style>
  </head>
  <body>
    <div class="print-memo">${memo || PRINT_CONFIG.messages.memoPreview}</div>

    <div id="print-map-wrapper">
      <div id="print-map"></div>
      <div class="print-map-attribution">${ATTRIBUTION_TEXT}</div>
    </div>

    <div class="mapion-logo">
      <img src="${logoUrl}" alt="${PRINT_CONFIG.messages.logoAlt}" style="height: 24px;" />
    </div>

    <div class="qr-section">
      <img id="qr-image" src="${qrUrl}" alt="${PRINT_CONFIG.messages.qrAlt}" style="width: 80px; height: 80px;" />
    </div>

    <script src="${PRINT_CONFIG.urls.maplibreJs}"></script>
    <script>
      const map = new maplibregl.Map({
        container: 'print-map',
        style: '${currentStyle}',
        center: [${currentCenter.lng}, ${currentCenter.lat}],
        zoom: ${currentZoom},
        pitch: ${currentPitch},
        bearing: ${currentBearing},
        attributionControl: false,
        preserveDrawingBuffer: true,
        interactive: false,
        fadeDuration: 0
      });

      map.once('idle', function() {
        setTimeout(function() {
          window.print();
          window.close();
        }, ${PRINT_CONFIG.delays.print});
      });
    </script>
  </body>
</html>
`;
/** 印刷用HTMLを生成する処理ここまで **/

/**
 * 通常印刷モーダルコンポーネント
 */
const PrintMapNormal = ({
  isPrintMapNormalOpen,
  setIsPrintMapNormalOpen,
  mapConfig,
}) => {

  // refs/states
  const mapRef = useRef(null);
  const printContentRef = useRef(null);
  const mapContainerRef = useRef(null);

  const currentCenter = mapConfig.center;
  const currentStyle = mapConfig.style || config.api.map.styles(DEFAULT_STYLE_NAME);

  const [currentZoom, setCurrentZoom] = useState(mapConfig.zoom);
  const [currentPitch, setCurrentPitch] = useState(mapConfig.pitch || 0);
  const [currentBearing, setCurrentBearing] = useState(mapConfig.bearing || 0);

  const [memo, setMemo] = useState('');
  const [paperSize, setPaperSize] = useState('a4-landscape');

  // サイズ選択メニュー
  const imageSize = PRINT_CONFIG.sizes.paperSizes[paperSize] || PRINT_CONFIG.sizes.defaultImageSize;
  const [width, height] = imageSize.split('x').map(Number);

  const pageSizeCss = getPageSizeCss(paperSize);

  // ロゴは別ウィンドウで開くため絶対パスで固定
  const logoUrl = MAPION_BASE_URL + config.icons.logoFullPath;

  // QRコード生成。地図に実際にアクセスするURL（これを使用するのはQRコードのみ）
  const styleName = mapConfig.styleName || DEFAULT_STYLE_NAME;
  let mapUrl = '';
  if (styleName === DEFAULT_STYLE_NAME) {
    mapUrl = `${MAPION_M2_URL}/${currentZoom.toFixed(DEFAULT_ZOOM_PRECISION)}/${currentCenter.lat}/${currentCenter.lng}/${currentPitch}/${currentBearing}`;
  } else {
    mapUrl = `${MAPION_M2_URL}/${styleName}/${currentZoom.toFixed(DEFAULT_ZOOM_PRECISION)}/${currentCenter.lat}/${currentCenter.lng}/${currentPitch}/${currentBearing}`;
  }
  const qrUrl = `${PRINT_CONFIG.urls.qrApi}?size=${PRINT_CONFIG.sizes.qrSize}&data=${encodeURIComponent(mapUrl)}`;

  // handlers
  const handleOverlayClick = (e) => {
    // print-settings-modal（背景オーバーレイ）自体がクリックされた場合のみ閉じる
    if (e.target.id === 'print-settings-modal') {
      setIsPrintMapNormalOpen(false);
    }
  };

  // メモプレビューはstateで管理
  const handleMemoChange = (e) => {
    setMemo(e.target.value);
  };

  // 用紙サイズ変更
  const handlePaperSizeChange = (e) => {
    setPaperSize(e.target.value);
  };

  // ズームイン・アウト
  const handleZoomIn = () => {
    setCurrentZoom(z => {
      const newZoom = Math.min(z + 1, 18);
      if (mapRef.current) mapRef.current.zoomTo(newZoom);
      return newZoom;
    });
  };
  const handleZoomOut = () => {
    setCurrentZoom(z => {
      const newZoom = Math.max(z - 1, 8);
      if (mapRef.current) mapRef.current.zoomTo(newZoom);
      return newZoom;
    });
  };

  // 印刷処理（印刷用HTMLを作成し、別ウィンドウで開いて印刷する）
  const handlePrint = () => {
    const printWindow = window.open(
      '',
      '_blank',
      PRINT_CONFIG.sizes.printWindow
    );
    if (!printWindow) {
      alert(PRINT_CONFIG.messages.popupBlocked);
      return;
    }

    // 印刷用の地図の高さ
    let printMapHeight = height;
    // 各用紙サイズごとに、1ページに収まるようにprint.jsの設定値で上書き
    printMapHeight = PRINT_CONFIG.printHeight[paperSize];

    // 印刷用HTMLを組み立てる
    const htmlContent = buildPrintHtml({
      memo,
      pageSizeCss,
      width,
      height: printMapHeight,
      logoUrl,
      qrUrl,
      currentStyle,
      currentCenter,
      currentZoom,
      currentPitch,
      currentBearing
    });

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // effects
  useEffect(() => {
    setCurrentZoom(mapConfig.zoom);
  }, [mapConfig.zoom]);

  useEffect(() => {
    setCurrentPitch(mapConfig.pitch || 0);
  }, [mapConfig.pitch]);

  useEffect(() => {
    setCurrentBearing(mapConfig.bearing || 0);
  }, [mapConfig.bearing]);

  // 印刷プレビュー用地図の初期化・設定
  useEffect(() => {
    if (!isPrintMapNormalOpen || !mapContainerRef.current) {
      return;
    }

    // 地図コンテナのサイズを選択された用紙サイズに合わせて設定
    mapContainerRef.current.style.width = `${width}px`;
    mapContainerRef.current.style.height = `${height}px`;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: currentStyle,
      center: [currentCenter.lng, currentCenter.lat],
      zoom: currentZoom,
      pitch: currentPitch,
      bearing: currentBearing,

      attributionControl: false,
      // 描画バッファを保持
      preserveDrawingBuffer: true,
      // アンチエイリアスを有効化
      antialias: true,
      // フェード効果を無効化
      fadeDuration: 0,
      // 選択されたサイズを使用
      width: width,
      height: height,
      // 印刷プレビューで移動操作不可にする（地図部分でドラッグすると移動してしまうのを防止）
      interactive: false
    });

    mapRef.current = map;

    // ロード完了後に強制的にサイズをリセット
    map.once('load', () => {
      // キャンバスを直接操作
      const canvas = mapContainerRef.current.querySelector('.maplibregl-canvas');
      if (canvas) {
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        // ピクセル比を考慮したサイズ設定
        const pixelRatio = window.devicePixelRatio || 1;
        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;
      }

      // 念のためリサイズを呼び出し
      map.resize();
      // 100ms後にもう一度リサイズを実行（遅延実行）
      setTimeout(() => {
        if (canvas) {
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
        }
        map.resize();
      }, 100);
    });

    return () => map.remove();
  }, [isPrintMapNormalOpen, currentStyle, currentPitch, currentBearing, width, height, currentCenter?.lat, currentCenter?.lng, currentZoom]);

  if (!isPrintMapNormalOpen) {
    return null;
  }

  // render:プレビュー画面
  return (
    <div
      id="print-settings-modal"
      onClick={handleOverlayClick}
    >
      <div className="print-modal-content" ref={printContentRef}>
        <div className="controls">
          <div className="control-group">
            <label>メモ</label>
            <textarea
              id="memo-input"
              placeholder={PRINT_CONFIG.messages.memoPlaceholder}
              value={memo}
              onChange={handleMemoChange}
            />
          </div>

          <div className="control-row">
            <div>
              <label>用紙</label>
              <select
                id="paper-size"
                value={paperSize}
                onChange={handlePaperSizeChange}
              >
                <option value="a4-portrait">A4 縦</option>
                <option value="a4-landscape">A4 横</option>
                <option value="a3-portrait">A3 縦</option>
                <option value="a3-landscape">A3 横</option>
              </select>
            </div>

            <div className="zoom-links">
              <button type="button" onClick={handleZoomOut}>広域</button>
              <button type="button" onClick={handleZoomIn}>詳細</button>
            </div>

            <button
              type="button"
              onClick={handlePrint}
              className="print-button"
            >
              印刷
            </button>
            <button
              onClick={() => setIsPrintMapNormalOpen(false)}
            >
              閉じる
            </button>
          </div>
        </div>

        <div className="print-memo" id="print-memo">
          {memo
            ? <span>{memo}</span>
            : <span className="empty">{PRINT_CONFIG.messages.memoPreview}</span>
          }
        </div>

        <div
          ref={mapContainerRef}
          className="print-map-container"
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}
        >
          <div className="print-map-attribution">
            {ATTRIBUTION_TEXT}
          </div>
        </div>

        <div className="mapion-logo">
          <img src={config.icons.logo} alt={PRINT_CONFIG.messages.logoAlt} />
        </div>

        {qrUrl && (
          <div className="qr-section">
            <img id="qr-image" src={qrUrl} alt={PRINT_CONFIG.messages.qrAlt} style={{ width: '80px', height: '80px' }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default PrintMapNormal;